import { type ComponentProps, type ComponentType, type ReactNode } from "react";
import { Check } from "@/shared/components/icons";
import { AuthCard } from "@/shared/components/ui/AuthCard";
import { cn } from "@/shared/lib/cn";

/** Badge color tones for the confirmation icon. */
type SuccessCardTone = "success" | "brand";

interface SuccessCardProps {
  /** Title to display (rendered as the card's H1, next to the icon). */
  title: string;
  /** Message to display next to the icon, under the title. */
  message: ReactNode;
  /** Icon shown inside the badge. Defaults to a check mark. */
  icon?: ComponentType<ComponentProps<"svg">>;
  /** Badge color tone — "success" (green) for completed actions, "brand" (teal) for informational confirmations like "email sent". */
  tone?: SuccessCardTone;
  /** Element above the logo in the brand header (e.g. a back link). Forwarded to {@link AuthCard}. */
  brandTop?: ReactNode;
  /** Footer spanning the card width (e.g. reCAPTCHA notice). Forwarded to {@link AuthCard}. */
  footer?: ReactNode;
  /** Optional actions (buttons, links) rendered full-width below the message */
  children?: ReactNode;
  /** Optional data-testid for the card wrapper */
  "data-testid"?: string;
}

const TONE_STYLES: Record<SuccessCardTone, { badge: string; icon: string }> = {
  success: { badge: "bg-green-100", icon: "text-green-600" },
  brand: { badge: "bg-brand-primary/10", icon: "text-brand-primary" },
};

// Header-scale title (not the 2rem hero scale) so it stays proportionate to the inline icon badge.
const TITLE_CLASS = "text-ink text-2xl leading-tight font-bold tracking-tight";

/**
 * SuccessCard — the single confirmation/terminal-state card.
 *
 * Used for every "you're done here" screen: registration sent, email verified,
 * password-reset email sent, activation email sent. One consistent composition:
 * the icon badge sits on the left and leads the headline + message on the right,
 * so the icon directly reinforces the title. Optional actions span full width below.
 *
 * Navigation (e.g. "Back to login") belongs in {@link brandTop}, not below the
 * message, so it sits in the same fixed header slot across all screens.
 *
 * The title renders as the page `<h1>`. Use this as a standalone/top-level card —
 * do not nest it inside another card or a page that already owns the page heading.
 *
 * @example
 * ```tsx
 * <SuccessCard title="Account created!" message="Check your email to confirm.">
 *   <Link to="/login" variant="button">Go to sign in</Link>
 * </SuccessCard>
 *
 * <SuccessCard
 *   icon={Mailbox}
 *   tone="brand"
 *   brandTop={<BackLink to="/login" label="Back to login" />}
 *   footer={<RecaptchaNotice />}
 *   title="Email sent!"
 *   message="Check your inbox for the reset link."
 * />
 * ```
 */
export function SuccessCard({
  title,
  message,
  icon: Icon = Check,
  tone = "success",
  brandTop,
  footer,
  children,
  "data-testid": dataTestId,
}: SuccessCardProps) {
  const toneStyle = TONE_STYLES[tone];

  return (
    <AuthCard brandTop={brandTop} data-testid={dataTestId} footer={footer}>
      <div className="flex flex-col items-start text-left">
        <div className="flex items-start gap-4">
          <div
            className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
              toneStyle.badge
            )}
          >
            <Icon aria-hidden className={cn("h-6 w-6", toneStyle.icon)} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <h1 className={TITLE_CLASS}>{title}</h1>
            <p className="mt-1 text-gray-600">{message}</p>
          </div>
        </div>
        {children && <div className="mt-6 w-full">{children}</div>}
      </div>
    </AuthCard>
  );
}
